import { z } from 'zod'
import interceptedAxios from '../utils/interceptedAxios'
import CustomToast from '../features/site-management/components/ToastComponent'

/**
 * File Download Service
 *
 * This service handles downloading files from Azure blob storage via the communications service.
 * It replaces the static file imports and provides dynamic file access.
 */

export const ZFileDownloadResponse = z.object({
  http_status: z.number(),
  data: z.instanceof(Blob),
  error: z.boolean(),
  error_message: z.optional(z.string())
})

export type FileDownloadResponse = z.infer<typeof ZFileDownloadResponse>

/**
 * Downloads a file from Azure blob storage and returns a blob URL
 * @param fileName - The name of the file to download
 * @param onSuccess - Callback function called on successful download with fileUrl and httpStatus
 * @param onFailure - Callback function called on failure with errorMessage and httpStatus
 */
export async function downloadFileFromAzure (
  fileName: string,
  onSuccess: (fileUrl: string, httpStatus: number) => any,
  onFailure: (errorMessage: string, httpStatus: number) => any
): Promise<void> {
  try {
    const url = `/communications-service/api/v1/udp/tiq/services/files/download/${fileName}`

    const response = await interceptedAxios.get(url, {
      timeout: 30000,
      responseType: 'blob' // Important for file downloads
    })

    // Determine MIME type based on file extension
    const mimeType = getMimeType(fileName)

    // Create a blob URL for the downloaded file with proper MIME type
    const blob = new Blob([response.data], { type: mimeType })
    const fileUrl = window.URL.createObjectURL(blob)

    onSuccess(fileUrl, response.status)
  } catch (error: any) {
    let errorMessage: string
    let httpStatus: number

    // istanbul ignore next
    if (error.response !== undefined) {
      errorMessage = error.response.data?.error_message ??
        error.response.data?.message ??
        `Failed to download ${fileName}`
      httpStatus = error.response.status
    } else {
      errorMessage = `Network error occurred while downloading ${fileName}`
      httpStatus = 500
    }

    // Show toast notification for the error
    CustomToast({
      type: 'error',
      heading: 'File Download Error',
      message: errorMessage
    })

    onFailure(errorMessage, httpStatus)
  }
}

/**
 * Determines MIME type based on file extension
 * @param fileName - The name of the file
 * @returns The MIME type string
 */
function getMimeType (fileName: string): string {
  const extension = fileName.toLowerCase().split('.').pop()

  switch (extension) {
    case 'pdf':
      return 'application/pdf'
    case 'txt':
      return 'text/plain'
    case 'doc':
      return 'application/msword'
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case 'xls':
      return 'application/vnd.ms-excel'
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case 'csv':
      return 'text/csv'
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'png':
      return 'image/png'
    default:
      return 'application/octet-stream'
  }
}

/**
 * Downloads the OSS License notice file from Azure blob storage
 * @param onSuccess - Callback function called on successful download
 * @param onFailure - Callback function called on failure
 */
export async function getLicenseFileUrl (
  onSuccess: (fileUrl: string, httpStatus: number) => any,
  onFailure: (errorMessage: string, httpStatus: number) => any
): Promise<void> {
  await downloadFileFromAzure('license_TIQ.txt', onSuccess, onFailure)
}

/**
 * Downloads the TIQ User Guide PDF from Azure blob storage
 * @param onSuccess - Callback function called on successful download
 * @param onFailure - Callback function called on failure
 */
export async function getUserGuideFileUrl (
  onSuccess: (fileUrl: string, httpStatus: number) => any,
  onFailure: (errorMessage: string, httpStatus: number) => any
): Promise<void> {
  await downloadFileFromAzure('TIQUserGuide.pdf', onSuccess, onFailure)
}

/**
 * Downloads the Sample Device Onboarding XLSX file from Azure blob storage
 * @param onSuccess - Callback function called on successful download
 * @param onFailure - Callback function called on failure
 */
export async function getSampleDeviceOnboardingFileUrl (
  onSuccess: (fileUrl: string, httpStatus: number) => any,
  onFailure: (errorMessage: string, httpStatus: number) => any
): Promise<void> {
  await downloadFileFromAzure('SampleDeviceOnboarding.xlsx', onSuccess, onFailure)
}
